<template>
	<Application :errorDetail="errorDetail" v-model:errorTitle="errorTitle" :successDetail="successDetail" v-model:successTitle="successTitle" :loading="loading">
		<template #navbar>
			<v-text-field variant="outlined" density="compact" clearable hide-details data-cy="operatorSearch"
				prepend-inner-icon="mdi-magnify"
				:placeholder="$t('text.search')"
				v-model="searchString"
				@keyup.enter="search()"
				@click:clear="clearSearch()"
			/>

			<v-btn class="gradientButton" size="small" elevation="0" data-cy="operatorAuditLogButton" v-if="hasFeature" @click="showAuditLog()">
				<v-icon>mdi-history</v-icon>
				<span class="d-none d-md-inline">{{ $t('text.operatorAuditFeed') }}</span>
			</v-btn>

			<v-btn class="gradientButton" size="small" elevation="0" data-cy="addOperatorButton" v-if="hasFeature && canManageOperators" @click="showAddOperator()">
				<v-icon>mdi-plus-circle</v-icon>
				<span class="d-none d-md-inline">{{ $t('text.addOperator') }}</span>
			</v-btn>
		</template>

		<v-card class="tableCard" v-if="hasFeature">
			<v-data-table fixed-header disable-sort data-cy="operatorsTable" :key="refreshCount"
				:headers="headers"
				:items="operators"
				:items-per-page="limit"
				class="elevation-0"
				:item-value="item => item"
			>
				<template #item="{ item }">
					<tr data-cy="operatorRow" @click="showEditOperator(item)" style="cursor: pointer;">
						<td><v-icon>mdi-account</v-icon></td>
						<td data-cy="operatorEmail">{{ item.email }}</td>
						<td data-cy="operatorName">{{ item.name }}</td>
						<td>
							<!-- icon badges instead of text chips — the app name shows on hover -->
							<v-tooltip v-for="app of item.applications" :key="app.id" :text="app.title || app.id" location="top">
								<template #activator="{ props: tp }">
									<span v-bind="tp" class="app-badge" data-cy="operatorAppBadge"><AppBadge :app="app" /></span>
								</template>
							</v-tooltip>
						</td>
						<td>
							<Tag v-for="tag of item.tags" :key="tag" :value="tag" data-cy="operatorTag" />
						</td>
						<td>
							<!-- one merged status column: a chip per system (core + partners), failures visible at a glance -->
							<v-chip v-if="item.markedForDeletion" size="x-small" color="orange" class="mr-1" data-cy="operatorDeletionPendingChip">
								{{ $t('text.operatorDeletionPending') }}
							</v-chip>
							<v-tooltip v-for="chip of systemChips(item)" :key="chip.system" location="top" content-class="system-chip-tooltip">
								<template #activator="{ props }">
									<v-chip v-bind="props" size="x-small" :color="chip.color" class="mr-1" :data-cy="`systemChip-${ chip.system }`">{{ chip.label }}</v-chip>
								</template>
								<div>{{ chip.tooltip }}</div>
								<!-- RC: the granted scope, one bullet per restricted dimension -->
								<ul v-if="chip.scope" class="scope-rows">
									<li v-for="row of chip.scope" :key="row.label"><b>{{ row.label }}:</b> {{ row.values }}</li>
								</ul>
							</v-tooltip>
						</td>
						<td>
							<v-btn v-if="canManageOperators" icon variant="text" size="small" data-cy="operatorRowDeleteButton" @click.stop="confirmDelete(item)">
								<v-icon>mdi-delete</v-icon>
							</v-btn>
						</td>
					</tr>
				</template>
				<template #bottom>
					<TablePaginator v-model="offset" :limit="limit" :total="total" @update:modelValue="getOperators()" :results="operators" />
				</template>
			</v-data-table>
		</v-card>

		<!-- one dialog for create + edit: same permission sections, account fields only on create -->
		<Dialog ref="operatorDialog" data-cy="operatorDialog"
			:width="'980px'"
			:height="'min(900px, calc(100vh - 40px))'"
			:title="dialogTitle"
			:confirm-label="dialogMode === 'create' ? $t('text.create') : $t('text.confirmSelection')"
			:confirm-handler="saveOperator"
			:is-valid="dialogValid"
			:cancel-label="$t('text.cancel')"
			:showClose="false"
			:cancel-handler="cancelDialog"
		>
			<template #content>
				<div class="operator-dialog" :class="{ 'account-suspended': accountSuspended }">
					<!-- account: fields on create, a compact status + lifecycle header on edit -->
					<div v-if="dialogMode === 'create'" class="account-create">
						<div>
							<div class="field-label">{{ $t('text.loginEmail') }} *</div>
							<v-text-field variant="outlined" density="compact" hide-details data-cy="operatorEmailInput" type="email" v-model="account.email" />
						</div>
						<div>
							<div class="field-label">{{ $t('text.name') }} *</div>
							<v-text-field variant="outlined" density="compact" hide-details data-cy="operatorNameInput" v-model="account.name" />
						</div>
						<div>
							<div class="field-label">{{ $t('text.preferredLanguage') }}</div>
							<v-select variant="outlined" density="compact" hide-details data-cy="operatorLanguageSelect"
								v-model="account.preferredLanguage"
								:items="[ 'de', 'en', 'fr', 'it', 'nl' ]"
							/>
						</div>
						<div class="account-create-2fa">
							<v-switch density="compact" hide-details color="primary" data-cy="operator2faSwitch"
								:label="$t('text.require2fa')"
								v-model="editSelection.twoFactorRequired"
							/>
						</div>
					</div>
					<div v-else-if="selectedOperator" class="account-header">
						<span class="account-identity">{{ selectedOperator.name }}</span>
						<v-chip size="small" data-cy="operatorStatusChip" :color="selectedOperator.enabled ? 'green' : 'red'">
							{{ selectedOperator.enabled ? $t('text.operatorActive') : $t('text.operatorDisabled') }}
						</v-chip>
						<v-switch density="compact" hide-details color="primary" class="account-2fa" data-cy="operator2faSwitch"
							:label="$t('text.require2fa')"
							v-model="editSelection.twoFactorRequired"
						/>
						<!-- lifecycle actions as tooltipped icons: full-text buttons made the header shout.
						     Hidden without the canManageOperators capability, and for your OWN account,
						     because the server refuses both (PEAK-50975 / MR !427 review). -->
						<div class="account-actions" v-if="canManageOperators && !isSelf">
							<v-tooltip :text="$t('text.resendRecoveryEmail')" location="top">
								<template #activator="{ props: tp }">
									<v-btn v-bind="tp" icon size="small" variant="text" data-cy="resendRecoveryButton" :disabled="!!recoveryCooldown[selectedOperator.id]" @click="resendRecovery()"><v-icon>mdi-email-sync</v-icon></v-btn>
								</template>
							</v-tooltip>
							<v-tooltip :text="selectedOperator.enabled ? $t('text.disableOperator') : $t('text.enableOperator')" location="top">
								<template #activator="{ props: tp }">
									<v-btn v-bind="tp" icon size="small" variant="text" data-cy="toggleEnabledButton" :color="selectedOperator.enabled ? 'red' : 'green'" @click="toggleEnabled()">
										<v-icon>{{ selectedOperator.enabled ? 'mdi-account-off' : 'mdi-account-check' }}</v-icon>
									</v-btn>
								</template>
							</v-tooltip>
							<v-tooltip :text="$t('text.deleteOperator')" location="top">
								<template #activator="{ props: tp }">
									<v-btn v-bind="tp" icon size="small" variant="text" color="red" data-cy="deleteOperatorButton" @click="cancelDialog(); confirmDelete(selectedOperator)"><v-icon>mdi-delete</v-icon></v-btn>
								</template>
							</v-tooltip>
						</div>
						<!-- suspended everywhere: the section toggles below show the DESIRED state that
						     re-enable will restore, not effective access — say so explicitly -->
						<div v-if="!selectedOperator.enabled" class="disabled-hint">
							<v-icon size="small" color="red">mdi-cancel</v-icon>
							{{ $t('text.operatorDisabledSystemsHint') }}
						</div>
					</div>

					<!-- one Disclosure per target system: the collapsed headers double as the health summary -->
					<Disclosure title="MyServices" data-cy="myServicesSection" :expanded="true" smallTitle>
						<template #header-accessory v-if="accountSuspended">
							<div class="header-accessory">
								<v-icon size="small" color="grey">mdi-lock</v-icon>
							</div>
						</template>
						<div class="field-label">{{ $t('text.applications') }}</div>
						<v-autocomplete variant="outlined" density="compact" hide-details multiple chips closable-chips data-cy="applicationsSelect"
							:disabled="accountSuspended"
							:items="ceiling.apps"
							:item-title="app => app.title || app.id"
							item-value="id"
							v-model="editSelection.applications"
						>
							<template #chip="{ props: chipProps, item }">
								<v-chip v-bind="chipProps"><AppBadge :app="item.raw" />{{ item.raw.title || item.raw.id }}</v-chip>
							</template>
							<template #item="{ props: itemProps, item }">
								<v-list-item v-bind="itemProps" :data-cy="`option-${ item.value }`">
									<template #prepend="{ isActive }">
										<v-checkbox-btn density="compact" :model-value="isActive" />
									</template>
									<template #title><AppBadge :app="item.raw" /><span style="padding-left: 6px;">{{ item.raw.title || item.raw.id }}</span></template>
								</v-list-item>
							</template>
						</v-autocomplete>
						<!-- MYS-7471: giving the Flexible Pricing Engine app provisions the operator in PS3 -->
						<div v-if="showFlexiblePricingStatus" class="flexible-pricing-status" data-cy="flexiblePricingSection">
							<ProvisioningStatus compact data-cy="flexiblePricingStatus" :label="$t('text.flexiblePricingStatus')" :state="provisioningFor('flexiblePricing')" @retry="retryProvisioning('flexiblePricing')" />
							<div v-if="isFailed('flexiblePricing')" class="section-error" data-cy="flexiblePricingError">
								<v-icon size="small" color="#f34545">mdi-alert-circle</v-icon>
								<span>{{ flexiblePricingNoDomain ? $t('text.flexiblePricingNoDomainHint') : provisioningFor('flexiblePricing')?.error }}</span>
							</div>
						</div>
						<div v-if="outOfScopeApps.length" class="out-of-scope">
							<div class="hint">{{ $t('text.operatorOutOfScopeHint') }}</div>
							<v-chip v-for="app of outOfScopeApps" :key="app.id" size="small" disabled class="mr-1" data-cy="outOfScopeApp">
								<AppBadge :app="app" />{{ app.title || app.id }}
							</v-chip>
						</div>
						<div class="field-label" style="margin-top: 12px;">{{ $t('text.tags') }}</div>
						<v-autocomplete variant="outlined" density="compact" hide-details multiple chips closable-chips data-cy="tagsSelect"
							:disabled="accountSuspended"
							:items="ceiling.tags"
							v-model="editSelection.tags"
						>
							<template #chip="{ props: chipProps, item }">
								<v-chip v-bind="chipProps"><Tag :value="item.value" />{{ item.value }}</v-chip>
							</template>
							<template #item="{ props: itemProps, item }">
								<v-list-item v-bind="itemProps" :data-cy="`option-${ item.value }`">
									<template #prepend="{ isActive }">
										<v-checkbox-btn density="compact" :model-value="isActive" />
									</template>
									<template #title><Tag :value="item.value" /><span style="padding-left: 6px;">{{ item.value }}</span></template>
								</v-list-item>
							</template>
						</v-autocomplete>
						<div v-if="outOfScopeTags.length" class="out-of-scope">
							<div class="hint">{{ $t('text.operatorOutOfScopeHint') }}</div>
							<Tag v-for="tag of outOfScopeTags" :key="tag" :value="tag" data-cy="outOfScopeTag" />
						</div>
					</Disclosure>

					<Disclosure v-if="showContentHubSection" ref="contentHubSection" data-cy="contentHubSection" title="Content Hub" smallTitle
						:expanded="!accountSuspended && (editSelection.contentHubEnabled || isFailed('contentHub'))"
					>
						<template #header-accessory>
							<div class="header-accessory">
								<v-icon v-if="accountSuspended" size="small" color="grey">mdi-lock</v-icon>
								<ProvisioningStatus compact data-cy="contentHubStatus" :state="provisioningFor('contentHub')" @retry="retryProvisioning('contentHub')" />
								<SectionToggle data-cy="contentHubToggle" :modelValue="!accountSuspended && editSelection.contentHubEnabled" :disabled="accountSuspended" @toggle="toggleContentHub" />
							</div>
						</template>
						<!-- the failure text lives in the body: the header only has room for chip + retry -->
						<div v-if="isFailed('contentHub')" class="section-error" data-cy="contentHubError">
							<v-icon size="small" color="#f34545">mdi-alert-circle</v-icon>
							<span>{{ provisioningFor('contentHub')?.error }}</span>
						</div>
						<template v-if="editSelection.contentHubEnabled && !accountSuspended">
							<div class="field-label">{{ $t('text.contentHubRole') }}</div>
							<v-select variant="outlined" density="compact" hide-details clearable data-cy="contentHubRoleSelect"
								:item-props="role => ({ 'data-cy': `option-${ role.id }` })"
								:items="chRoles.roles"
								item-title="name"
								item-value="id"
								:model-value="editSelection.contentHubRole"
								@update:modelValue="setContentHubRole"
							/>
						</template>
						<div v-else class="hint">{{ $t('text.contentHubRole') }}</div>
					</Disclosure>

					<Disclosure ref="reportCenterSection" data-cy="reportCenterSection" title="Report Center" smallTitle
						:expanded="!accountSuspended && (editSelection.reportCenter.enabled || isFailed('reportCenter'))"
					>
						<template #header-accessory>
							<div class="header-accessory">
								<v-icon v-if="accountSuspended" size="small" color="grey">mdi-lock</v-icon>
								<ProvisioningStatus compact data-cy="reportCenterStatus" :state="provisioningFor('reportCenter')" @retry="retryProvisioning('reportCenter')" />
								<SectionToggle data-cy="reportCenterToggle" :modelValue="!accountSuspended && editSelection.reportCenter.enabled" :disabled="accountSuspended" @toggle="toggleReportCenter" />
							</div>
						</template>
						<!-- the failure text lives in the body: the header only has room for chip + retry -->
						<div v-if="isFailed('reportCenter')" class="section-error" data-cy="reportCenterError">
							<v-icon size="small" color="#f34545">mdi-alert-circle</v-icon>
							<span>{{ provisioningFor('reportCenter')?.error }}</span>
						</div>
						<template v-if="editSelection.reportCenter.enabled && !accountSuspended">
							<div class="hint">{{ $t('text.revenueDetailReportScope') }}</div>
							<div class="scope-grid">
								<div v-for="dimension of rdrDimensions" :key="dimension.key">
									<div class="field-label">{{ dimension.label }}</div>
									<!-- items come from ARC (ARC-1124); free text stays possible as fallback, and
									     reaches item-props as a bare string rather than an {id,label} -->
									<v-combobox variant="outlined" density="compact" hide-details multiple chips closable-chips clearable
										:data-cy="`rcScope-${ dimension.key }`"
										:item-props="value => ({ 'data-cy': `option-${ value?.id ?? value }` })"
										:items="rcDimensionValues[dimension.key] ?? []"
										item-title="label"
										item-value="id"
										:return-object="false"
										v-model="editSelection.reportCenter.revenueDetailReport[dimension.key]"
									/>
								</div>
							</div>
						</template>
						<div v-else class="hint">{{ $t('text.reportCenterEnable') }}</div>
					</Disclosure>

					<Disclosure v-if="showPeakSection" ref="peaksolutionSection" data-cy="peaksolutionSection" title="Peaksolution" smallTitle
						:expanded="!accountSuspended && (editSelection.peaksolution.enabled || isFailed('peaksolution') || isFailed('metis'))"
					>
						<template #header-accessory>
							<div class="header-accessory">
								<v-icon v-if="accountSuspended" size="small" color="grey">mdi-lock</v-icon>
								<!-- one combined chip while healthy; per-system chips + retry only on failure -->
								<template v-if="isFailed('peaksolution') || isFailed('metis')">
									<ProvisioningStatus compact data-cy="peaksolutionStatus" v-if="isFailed('peaksolution')" :state="provisioningFor('peaksolution')" @retry="retryProvisioning('peaksolution')" />
									<ProvisioningStatus compact data-cy="metisStatus" v-if="isFailed('metis')" label="Metis" :state="provisioningFor('metis')" @retry="retryProvisioning('metis')" />
								</template>
								<ProvisioningStatus compact data-cy="peaksolutionStatus" v-else :state="provisioningFor('peaksolution') || provisioningFor('metis')" />
								<SectionToggle data-cy="peaksolutionToggle" :modelValue="!accountSuspended && editSelection.peaksolution.enabled" :disabled="accountSuspended" @toggle="togglePeaksolution" />
							</div>
						</template>
						<!-- the failure text lives in the body: the header only has room for chip + retry -->
						<div v-if="isFailed('peaksolution') || isFailed('metis')" class="section-error" data-cy="peaksolutionError">
							<v-icon size="small" color="#f34545">mdi-alert-circle</v-icon>
							<span>{{ peakErrorMessage() }}</span>
						</div>
						<template v-if="editSelection.peaksolution.enabled && !accountSuspended">
							<div class="field-label">{{ $t('text.peaksolutionRole') }}</div>
							<v-select
								variant="outlined" density="compact" hide-details clearable data-cy="peaksolutionRoleSelect"
								:item-props="role => ({ 'data-cy': `option-${ role.id }` })"
								:items="peakRoles"
								item-title="name"
								item-value="id"
								v-model="editSelection.peaksolution.role"
							/>
						</template>
						<div v-else class="hint">{{ $t('text.peaksolutionEnable') }}</div>
						<!-- MYS-7148: Peak backoffice login through Metis (api2 asks Metis as staff with the
						     operator's PEAK username). Own account: one click. Another operator's account:
						     confirmed first — the Peak session is THEIRS and the login is audited -->
						<div class="peak-login" v-if="dialogMode === 'edit'">
							<!-- icon-only: the full label lives in the tooltip (the German label overflows a half-width column) -->
							<v-tooltip :text="peakLoginHint || (peakLoginIsSelf ? $t('text.loginToPeaksolution') : $t('text.loginToPeaksolutionAs'))" location="top">
								<template #activator="{ props: tooltipProps }">
									<span v-bind="tooltipProps">
										<v-btn size="small" variant="outlined" icon data-cy="peakLoginButton" :disabled="!!peakLoginHint" :loading="peakLoginPending" @click="requestPeakLogin()">
											<v-icon>{{ peakLoginIsSelf ? 'mdi-login-variant' : 'mdi-account-arrow-right' }}</v-icon>
										</v-btn>
									</span>
								</template>
							</v-tooltip>
							<span class="peak-login-label">{{ peakLoginIsSelf ? $t('text.loginToPeaksolution') : $t('text.loginToPeaksolutionAs') }}</span>
						</div>
					</Disclosure>

					<!-- MYS-7481: this operator's audit history, collapsed by default. The list mounts
					     (and loads) on the first expand only; the dialog content unmounts on close and
					     showEditOperator resets historyOpened, so another operator never sees stale entries.
					     update:modelValue falls through to Disclosure's v-expansion-panels (0 = expanded, undefined = collapsed) -->
					<Disclosure v-if="dialogMode === 'edit' && selectedOperator" class="history-section" data-cy="operatorHistorySection"
						:title="$t('text.operatorHistory')" smallTitle
						@update:modelValue="historyOpened = historyOpened || $event != null"
					>
						<OperatorAuditList v-if="historyOpened" :key="selectedOperator.id"
							data-cy="operatorHistoryList"
							:target-user-id="selectedOperator.id"
							hide-target
							:operators="operators"
							:dimension-values="rcDimensionValues"
							:peak-roles="peakRoles"
							@loading="loading = $event"
							@error="onAuditLogError"
						/>
					</Disclosure>
				</div>
			</template>
		</Dialog>

		<!-- audit feed: who did what to whom, when — read-only, complete (not ceiling-filtered) -->
		<Dialog ref="auditDialog" data-cy="auditDialog"
			:width="'860px'"
			:height="'min(760px, calc(100vh - 40px))'"
			:title="$t('text.operatorAuditFeed')"
			:cancel-label="$t('text.close')"
			:cancel-handler="() => { $refs.auditDialog.show = false }"
			:showClose="false"
		>
			<template #content>
				<OperatorAuditFilters v-model="auditFilters" :operators="auditOperators" :actors="auditActors" />
				<OperatorAuditList
					:operators="auditOperators"
					:filters="auditFilters"
					:dimension-values="rcDimensionValues"
					:peak-roles="peakRoles"
					@loading="loading = $event"
					@error="onAuditLogError"
					@actors="auditActors = $event"
				/>
			</template>
		</Dialog>

		<!-- on-behalf Peak login: the session belongs to the other operator, so confirm first -->
		<Dialog ref="peakLoginDialog" data-cy="peakLoginDialog"
			:width="'540px'"
			:height="'auto'"
			:title="$t('text.loginToPeaksolutionAs')"
			:confirm-label="$t('text.confirm')"
			:confirm-handler="loginToPeaksolution"
			:cancel-label="$t('text.cancel')"
			:showClose="false"
		>
			<template #content>
				<div style="padding: 10px; width: 100%;" v-if="selectedOperator">
					<p style="font-weight: bold;">{{ selectedOperator.email }}</p>
					<p style="margin-top: 8px;">{{ $t('text.peaksolutionLoginOnBehalfConfirmation') }}</p>
				</div>
			</template>
		</Dialog>

		<!-- delete = remove from ALL systems; a partial failure leaves a retryable 'deletion pending' operator -->
		<Dialog ref="deleteDialog" data-cy="deleteOperatorDialog"
			:width="'540px'"
			:height="'auto'"
			:title="$t('text.deleteOperator')"
			:confirm-label="$t('text.deleteOperator')"
			:confirm-handler="deleteOperator"
			:cancel-label="$t('text.cancel')"
			:cancel-handler="cancelDelete"
			:showClose="false"
			:isDelete="true"
		>
			<template #content>
				<div style="padding: 10px; width: 100%;" v-if="deleteCandidate">
					<p style="font-weight: bold;">{{ deleteCandidate.email }}</p>
					<p style="margin-top: 8px;">{{ $t('text.operatorDeleteConfirmation') }}</p>
					<div v-if="deleteCandidate.markedForDeletion" style="margin-top: 8px;">
						<div class="hint">{{ $t('text.operatorDeletePartialFailure') }}</div>
						<v-tooltip v-for="chip of systemChips(deleteCandidate)" :key="chip.system" :text="chip.tooltip" location="top">
							<template #activator="{ props }">
								<v-chip v-bind="props" size="x-small" :color="chip.color" class="mr-1" :data-cy="`deleteSystemChip-${ chip.system }`">{{ chip.label }}</v-chip>
							</template>
						</v-tooltip>
					</div>
				</div>
			</template>
		</Dialog>
	</Application>
</template>

<script>
import Common from '@/mixins/Common.vue'
import Application from '../Application.vue'
import TablePaginator from '@/components/common/TablePaginator.vue'
import Tag from '@/components/common/Tag.vue'
import Dialog from '@/components/common/Dialog.vue'
import Disclosure from '@/components/common/Disclosure.vue'
import ProvisioningStatus from './ProvisioningStatus.vue'
import SectionToggle from './SectionToggle.vue'
import AppBadge from './AppBadge.vue'
import OperatorAuditList from './OperatorAuditList.vue'
import OperatorAuditFilters from './OperatorAuditFilters.vue'
import { Features } from '../../../../../shared/features'
import OperatorsExecutive from '../../../../../api2/src/modules/operators/OperatorsExecutive'
import { RecoveryEmailRateLimitedError } from '../../../../../api2/src/framework/rmi-errors'

const SYSTEM_LABELS = {
	contentHub: { label: 'CH', name: 'Content Hub' },
	reportCenter: { label: 'RC', name: 'Report Center' },
	peaksolution: { label: 'PS', name: 'Peaksolution' },
	metis: { label: 'MT', name: 'Metis' },
	flexiblePricing: { label: 'FP', name: 'Flexible Pricing (PS3)' },
	// core systems: always shown in the list's status column; they also carry
	// deletion-failure states when a delete is pending
	keycloak: { label: 'KC', name: 'Keycloak' },
	contentManager: { label: 'MYS', name: 'MyServices' },
}

const PARTNER_SYSTEMS = [ 'contentHub', 'reportCenter', 'peaksolution', 'metis', 'flexiblePricing' ]

// the Flexible Pricing Engine appComponent, recognised by link (its entry id differs per environment)
const FLEXIBLE_PRICING_APP_LINK = '/app/DiscountEditor'

export default {
	name: 'OperatorsView',
	mixins: [ Common ],
	components: { Application, TablePaginator, Tag, Dialog, Disclosure, ProvisioningStatus, SectionToggle, AppBadge, OperatorAuditList, OperatorAuditFilters },
	data() { return {
		loading: false,
		errorTitle: '',
		errorDetail: '',
		successTitle: '',
		successDetail: '',
		// operator id → true while the resend button is cooling down (MYS-7478; the server enforces the real limit)
		recoveryCooldown: {},

		limit: 15,
		offset: 0,
		searchString: '',

		operators: [],
		total: 0,
		refreshCount: 0,
		// the edit dialog's "Verlauf" section: false until first expanded (no audit request before)
		historyOpened: false,
		// the global Aktivitätsprotokoll: filter bar state, its picker options (all of the
		// client's operators, not the table's page; the admins seen in the log)
		auditFilters: {},
		auditOperators: [],
		auditActors: [],

		ceiling: { apps: [], tags: [], hasAllTags: false, canManageOperators: false },
		chRoles: { configured: false, roles: [] },
		peakRoles: [],
		rcDimensionValues: {},
		// which client the dialog option data (ceiling/roles/dimensions) was loaded for
		dialogDataClientId: null,


		dialogMode: 'edit',
		dialogTitle: '',
		selectedOperator: null,
		deleteCandidate: null,
		account: { email: '', name: '', preferredLanguage: 'de' },
		peakLoginPending: false,
		editSelection: this.emptySelection(),
	}},
	computed: {
		// the capability that gates administering OTHER operators — set per user in the Toolbox
		// User Manager. The server enforces it; hiding the actions here just avoids offering
		// buttons that would be refused.
		canManageOperators() {
			return !!this.ceiling.canManageOperators
		},
		isSelf() {
			return this.selectedOperator?.id === this.$store.state.loggedInUser?.sys?.id
		},
		peakLoginIsSelf() {
			return this.isSelf
		},
		// why the Peak login button is disabled (empty = enabled): PEAK must have provisioned
		// the account (username persisted by api2) and the operator must not be disabled
		peakLoginHint() {
			const op = this.selectedOperator
			if (!op || !op.enabled || !op.peaksolution?.enabled || !op.peaksolution?.username) return this.$t('text.peaksolutionLoginNotProvisioned')
			return ''
		},
		// create needs the starred account fields; edit has nothing mandatory
		dialogValid() {
			if (this.dialogMode !== 'create') return true
			const email = (this.account.email ?? '').trim()
			const name = (this.account.name ?? '').trim()
			return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && name.length > 0
		},
		hasFeature() {
			return this.featureEnabled(Features.OPERATORS)
		},
		// a disabled account is suspended in every system — the dialog presents the sections
		// as off/locked, while the stored desired state survives for re-enable
		accountSuspended() {
			return this.dialogMode === 'edit' && !!this.selectedOperator && !this.selectedOperator.enabled
		},
		// grants on the operator that the acting user may not manage — shown disabled, preserved server-side
		outOfScopeApps() {
			if (!this.selectedOperator) return []
			const ceilingIds = this.ceiling.apps.map(a => a.id)
			return this.selectedOperator.applications.filter(a => !ceilingIds.includes(a.id))
		},
		// next to the app while it is selected, and while its PS3 setup has failed (retry stays reachable)
		showFlexiblePricingStatus() {
			if (this.dialogMode !== 'edit') return false
			const appIds = this.ceiling.apps.filter(app => app.link === FLEXIBLE_PRICING_APP_LINK).map(app => app.id)
			return this.editSelection.applications.some(id => appIds.includes(id)) || this.isFailed('flexiblePricing')
		},
		// api2 marks a client without a PS3 domain with this code; shown as a support hint
		flexiblePricingNoDomain() {
			return this.provisioningFor('flexiblePricing')?.code === 'noBillingDomain'
		},
		outOfScopeTags() {
			if (!this.selectedOperator || this.ceiling.hasAllTags) return []
			return this.selectedOperator.tags.filter(t => !this.ceiling.tags.includes(t))
		},
		// sections are gated by the CLIENT's system capability (CH space / PEAK installation),
		// not by MYS app assignment — CH/RC/Peak are not MyServices apps
		showContentHubSection() {
			return this.chRoles.configured
		},
		showPeakSection() {
			return this.peakRoles.length > 0
		},
		// salesChannels are ARC's websites (ARC-1164): the picker offers them, the ids go back as-is
		rdrDimensions() {
			return [
				{ key: 'salesChannels', label: this.$t('text.salesChannels') },
				{ key: 'regions', label: this.$t('text.regions') },
				{ key: 'serviceProviders', label: this.$t('text.serviceProviders') },
				{ key: 'products', label: this.$t('text.products') },
				{ key: 'resellers', label: this.$t('text.resellers') },
			]
		},
		headers() {
			return [
				{ title: '', key: 'icon', width: '32' },
				{ title: this.$t('text.loginEmail'), key: 'email', width: '22%' },
				{ title: this.$t('text.name'), key: 'name', width: '18%' },
				{ title: this.$t('text.applications'), key: 'applications', width: '20%' },
				{ title: this.$t('text.tags'), key: 'tags', width: '15%' },
				{ title: this.$t('text.status'), key: 'systems', width: '25%' },
				{ title: '', key: 'actions', width: '48' },
			]
		},
	},
	methods: {
		emptySelection() {
			return {
				applications: [],
				tags: [],
				twoFactorRequired: false,
				// contentHubEnabled is UI-only staging: the saved payload derives enabled from the role
				contentHubEnabled: false,
				contentHubRole: null,
				reportCenter: this.emptyReportCenter(),
				peaksolution: { enabled: false, role: null },
			}
		},
		emptyReportCenter() {
			return { enabled: false, revenueDetailReport: { salesChannels: [], regions: [], serviceProviders: [], products: [], resellers: [] } }
		},
		// one chip per system: the core (MYS entry, Keycloak account) always shows; partner
		// systems show when enabled or when a provisioning state exists. Failure wins the color.
		systemChips(operator) {
			const chips = []
			const statusTexts = {
				provisioned: this.$t('text.provisioningProvisioned'),
				failed: this.$t('text.provisioningFailed'),
				pending: this.$t('text.provisioningPending'),
			}

			// MYS = the Content Manager entry; a listed operator always has one. Deletion
			// failures land on the same key ('contentManager'). A disabled Keycloak account
			// blocks the MYS login too, so the MYS chip goes grey with it.
			const cmState = operator.provisioning?.contentManager
			const mysSuspended = !operator.enabled && cmState?.status !== 'failed'
			chips.push({
				system: 'contentManager',
				label: SYSTEM_LABELS.contentManager.label,
				color: cmState?.status === 'failed' ? 'red' : mysSuspended ? 'grey' : 'green',
				tooltip: `${ SYSTEM_LABELS.contentManager.name }: `
					+ (cmState?.status === 'failed' ? `${ statusTexts.failed } — ${ cmState.error ?? '' }`
						: mysSuspended ? this.$t('text.operatorDisabled') : statusTexts.provisioned)
					+ ` · ${ operator.applications.length } ${ this.$t('text.applications') } · ${ operator.tags.length } ${ this.$t('text.tags') }`,
			})

			// KC carries the account status (active/disabled); deletion failures win
			const kcState = operator.provisioning?.keycloak
			chips.push({
				system: 'keycloak',
				label: SYSTEM_LABELS.keycloak.label,
				color: kcState?.status === 'failed' ? 'red' : operator.enabled ? 'green' : 'red',
				tooltip: `${ SYSTEM_LABELS.keycloak.name }: `
					+ (kcState?.status === 'failed'
						? `${ statusTexts.failed } — ${ kcState.error ?? '' }`
						: (operator.enabled ? this.$t('text.operatorActive') : this.$t('text.operatorDisabled'))),
			})

			const enabledFor = {
				// the CH role is retained on disable — enabled-ness comes from the explicit flag
				contentHub: !!operator.contentHubEnabled,
				reportCenter: !!operator.reportCenter?.enabled,
				peaksolution: !!operator.peaksolution?.enabled,
				metis: !!operator.peaksolution?.enabled,
			}
			// what is granted in that system, so the list answers "which permission?" on hover
			const grantFor = {
				contentHub: operator.contentHubRoleName ?? operator.contentHubRole,
				peaksolution: this.peakRoleName(operator.peaksolution?.role),
			}
			for (const system of PARTNER_SYSTEMS) {
				const state = operator.provisioning?.[system]
				if (!state && !enabledFor[system]) continue
				const status = state?.status ?? 'pending'
				const grant = grantFor[system] ? ` (${ grantFor[system] })` : ''
				// a disabled account is suspended in every system: show grey (failures stay red so
				// the retry affordance is not hidden), and say "disabled" instead of "provisioned"
				const suspended = !operator.enabled && status !== 'failed'
				chips.push({
					system,
					label: SYSTEM_LABELS[system].label,
					color: suspended ? 'grey' : status === 'provisioned' ? 'green' : status === 'failed' ? 'red' : 'grey',
					tooltip: `${ SYSTEM_LABELS[system].name }${ grant }: `
						+ (suspended ? this.$t('text.operatorDisabled') : `${ statusTexts[status] }${ state?.error ? ' — ' + state.error : '' }`),
					scope: system === 'reportCenter' ? this.rcScopeRows(operator) : null,
				})
			}
			return chips
		},
		// the RC grant per dimension, ids resolved to their ARC labels; unrestricted dimensions are omitted
		rcScopeRows(operator) {
			const scope = operator.reportCenter?.revenueDetailReport
			if (!scope) return null
			const rows = []
			for (const dimension of this.rdrDimensions) {
				const values = scope[dimension.key] ?? []
				if (!values.length) continue
				const labels = values.map(value => this.rcDimensionValues[dimension.key]?.find(item => item.id === value)?.label ?? value)
				rows.push({ label: dimension.label, values: labels.join(', ') })
			}
			return rows.length ? rows : null
		},
		// own account: straight in; someone else's: confirm first (their session, audited)
		requestPeakLogin() {
			if (this.peakLoginHint || this.peakLoginPending) return
			if (this.peakLoginIsSelf) return this.loginToPeaksolution()
			this.$refs.peakLoginDialog.show = true
		},
		// the Metis login URL is single-use and short-lived: open the tab synchronously (popup
		// blockers allow it inside the click) and point it at the URL once api2 has it
		async loginToPeaksolution() {
			if (this.peakLoginHint || this.peakLoginPending) return true
			this.peakLoginPending = true
			const tab = window.open('', '_blank')
			try {
				const executive = new OperatorsExecutive(this)
				const { url } = await executive.getPeaksolutionLoginUrl(this.$store.state.selectedClient.sys.id, { userId: this.selectedOperator.id })
				if (tab) tab.location = url
				else window.open(url, '_blank')
			}
			catch (error) {
				if (tab) tab.close()
				if (error.response?.status == 401) return this.$emit('show-login')
				this.showError(error)
			}
			this.peakLoginPending = false
			return true // closes the confirmation dialog when used as its confirm handler
		},
		peakRoleName(roleId) {
			if (!roleId) return undefined
			return this.peakRoles.find(role => role.id === roleId)?.name ?? roleId
		},
		provisioningFor(system) {
			return this.selectedOperator?.provisioning?.[system]
		},
		isFailed(system) {
			return this.provisioningFor(system)?.status === 'failed'
		},
		// both linked systems can fail at once — show both errors, not just the first
		peakErrorMessage() {
			return [ this.provisioningFor('peaksolution')?.error, this.provisioningFor('metis')?.error ]
				.filter(Boolean).join(' — ')
		},
		// enabling a system from its header expands the section so configuring it is one motion.
		// The switches only stage editSelection — everything is applied on confirm, like before.
		// CH access itself is role-driven: off clears the role, on opens the section to pick one
		toggleContentHub(enabled) {
			this.editSelection.contentHubEnabled = enabled
			if (!enabled) this.editSelection.contentHubRole = null
			else this.$refs.contentHubSection?.open()
		},
		setContentHubRole(role) {
			this.editSelection.contentHubRole = role ?? null
			this.editSelection.contentHubEnabled = !!role || this.editSelection.contentHubEnabled
		},
		toggleReportCenter(enabled) {
			this.editSelection.reportCenter.enabled = enabled
			if (enabled) this.$refs.reportCenterSection?.open()
		},
		togglePeaksolution(enabled) {
			this.editSelection.peaksolution.enabled = enabled
			if (enabled) this.$refs.peaksolutionSection?.open()
		},
		async getOperators() {
			if (!this.hasFeature) return
			this.loading = true
			try {
				const executive = new OperatorsExecutive(this)
				const result = await executive.getOperators(this.$store.state.selectedClient.sys.id, {
					skip: this.offset,
					limit: this.limit,
					searchString: this.searchString ?? '',
				})
				this.operators = result.operators
				this.total = result.total
				this.refreshCount++
			}
			catch (error) {
				if (error.response?.status == 401) return this.$emit('show-login')
				this.showError(error)
			}
			this.loading = false
		},
		async loadCeiling() {
			try {
				const executive = new OperatorsExecutive(this)
				this.ceiling = await executive.getPermissionCeiling(this.$store.state.selectedClient.sys.id)
			}
			catch (error) {
				if (error.response?.status == 401) return this.$emit('show-login')
				this.showError(error)
			}
		},
		async loadChRoles() {
			try {
				const executive = new OperatorsExecutive(this)
				this.chRoles = await executive.getContentHubRoles(this.$store.state.selectedClient.sys.id)
			}
			catch (error) {
				// CH being down must not break the whole view — the section just stays hidden
				console.error('loadChRoles failed', error)
			}
		},
		async loadPeakRoles() {
			try {
				const executive = new OperatorsExecutive(this)
				this.peakRoles = await executive.getPeakRoles(this.$store.state.selectedClient.sys.id)
			}
			catch (error) {
				console.error('loadPeakRoles failed', error)
			}
		},
		async loadRcDimensionValues() {
			try {
				const executive = new OperatorsExecutive(this)
				this.rcDimensionValues = await executive.getReportCenterDimensions(this.$store.state.selectedClient.sys.id)
			}
			catch (error) {
				// without items the scope comboboxes simply stay free-text
				console.error('loadRcDimensionValues failed', error)
			}
		},
		async retryProvisioning(system) {
			this.loading = true
			try {
				const executive = new OperatorsExecutive(this)
				const state = await executive.retryProvisioning(this.$store.state.selectedClient.sys.id, {
					userId: this.selectedOperator.id,
					system,
				})
				this.selectedOperator.provisioning = { ...this.selectedOperator.provisioning, [system]: state }
				await this.getOperators()
			}
			catch (error) {
				if (error.response?.status == 401) return this.$emit('show-login')
				this.showError(error)
			}
			this.loading = false
		},
		// the list loads its first page when the dialog mounts it
		showAuditLog() {
			this.auditFilters = {}
			this.auditOperators = this.operators
			this.$refs.auditDialog.show = true
			this.loadAuditOperators()
			// scope ids and the Peaksolution role id in the change lines are named from these; until
			// they arrive (or if ARC / PEAK is unreachable) the lines show the raw ids
			if (!Object.keys(this.rcDimensionValues).length) this.loadRcDimensionValues()
			if (!this.peakRoles.length) this.loadPeakRoles()
		},
		// the operator filter offers every current operator of the client (getOperators reads up
		// to 1000 from the CM anyway); until they arrive it offers the table's page
		async loadAuditOperators() {
			try {
				const executive = new OperatorsExecutive(this)
				const result = await executive.getOperators(this.$store.state.selectedClient.sys.id, { skip: 0, limit: 1000, searchString: '' })
				this.auditOperators = result.operators
			}
			catch (error) {
				this.onAuditLogError(error)
			}
		},
		onAuditLogError(error) {
			if (error.response?.status == 401) return this.$emit('show-login')
			this.showError(error)
		},
		// the dialog's option data (ceiling, roles, dimension values) is loaded once per client;
		// a transient load failure (or a client switch) must not leave the dialog permanently
		// empty, so every dialog open re-ensures it before rendering pickers
		async ensureDialogData() {
			const clientId = this.$store.state.selectedClient.sys.id
			const hasCeiling = this.ceiling.hasAllTags || this.ceiling.apps.length || this.ceiling.tags.length
			if (this.dialogDataClientId === clientId && hasCeiling) return
			this.dialogDataClientId = clientId
			await Promise.all([
				this.loadCeiling(),
				this.loadChRoles(),
				this.loadPeakRoles(),
				this.loadRcDimensionValues(),
			])
		},
		async showAddOperator() {
			this.loading = true
			await this.ensureDialogData()
			this.loading = false
			this.dialogMode = 'create'
			this.selectedOperator = null
			this.account = { email: '', name: '', preferredLanguage: 'de' }
			this.editSelection = this.emptySelection()
			// on create the system sections start toggled on, so their fields are visible without
			// an extra click. This is UI staging only: saveOperator diffs against the same defaults,
			// so an untouched (or re-disabled) section is not sent and provisions nothing
			this.editSelection.contentHubEnabled = true
			this.editSelection.reportCenter.enabled = true
			this.editSelection.peaksolution.enabled = true
			this.dialogTitle = this.$t('text.addOperator')
			this.$refs.operatorDialog.show = true
		},
		confirmDelete(operator) {
			this.deleteCandidate = operator
			this.$refs.deleteDialog.show = true
		},
		cancelDelete() {
			this.$refs.deleteDialog.show = false
		},
		// delete is idempotent server-side: for a 'deletion pending' operator this IS the retry
		async deleteOperator() {
			this.loading = true
			try {
				const executive = new OperatorsExecutive(this)
				const result = await executive.deleteOperator(this.$store.state.selectedClient.sys.id, { userId: this.deleteCandidate.id })
				this.$refs.deleteDialog.show = false
				if (result.deleted) {
					this.successTitle = this.$t('text.SUCCESS')
					this.successDetail = this.$t('text.operatorDeleted')
				}
				else {
					this.errorTitle = this.$t('text.ERROR')
					this.errorDetail = this.$t('text.operatorDeletePartialFailure')
				}
				await this.getOperators()
			}
			catch (error) {
				if (error.response?.status == 401) return this.$emit('show-login')
				this.showError(error)
			}
			this.loading = false
		},
		async showEditOperator(operator) {
			// a half-deleted operator must not be edited — offer the deletion retry instead
			if (operator.markedForDeletion) return this.confirmDelete(operator)
			// the ceiling filters the pre-selection below — with a stale/empty ceiling the
			// operator's grants would render deselected and be lost on save
			this.loading = true
			await this.ensureDialogData()
			this.loading = false
			this.dialogMode = 'edit'
			this.selectedOperator = operator
			this.historyOpened = false
			const ceilingAppIds = this.ceiling.apps.map(a => a.id)
			this.editSelection = {
				applications: operator.applications.map(a => a.id).filter(id => ceilingAppIds.includes(id)),
				tags: operator.tags.filter(t => this.ceiling.hasAllTags || this.ceiling.tags.includes(t)),
				twoFactorRequired: operator.twoFactorRequired,
				// the server retains the CH role on disable (keeps removal retryable), so
				// enabled-ness comes from the explicit flag — never derive it from the role
				contentHubEnabled: operator.contentHubEnabled ?? !!operator.contentHubRole,
				contentHubRole: operator.contentHubRole ?? null,
				reportCenter: operator.reportCenter
					? JSON.parse(JSON.stringify({ ...this.emptyReportCenter(), ...operator.reportCenter, revenueDetailReport: { ...this.emptyReportCenter().revenueDetailReport, ...(operator.reportCenter.revenueDetailReport ?? {}) } }))
					: this.emptyReportCenter(),
				peaksolution: operator.peaksolution
					? { enabled: !!operator.peaksolution.enabled, role: operator.peaksolution.role ?? null }
					: { enabled: false, role: null },
			}
			this.dialogTitle = `${ this.$t('text.editPermissions') }<p style="font-size:10pt;color:#646464">${ operator.email }</p>`
			this.$refs.operatorDialog.show = true
		},
		cancelDialog() {
			this.$refs.operatorDialog.show = false
		},
		// builds the permissions payload from the current selection (create: vs empty; edit: vs the operator)
		buildPermissionsRequest(userId, previous) {
			const request = {
				userId,
				applications: this.editSelection.applications,
				tags: this.editSelection.tags,
			}
			// a CH change is a role change OR an enabled flip around a known role — the retained-role
			// disable and the same-role re-enable both leave the role equal, so role-diff alone misses them
			const previousChRole = previous.contentHubRole ?? null
			const previousChEnabled = previous.contentHubEnabled ?? !!previousChRole
			const chRoleChanged = this.editSelection.contentHubRole !== previousChRole
			const chEnabledChanged = !!previousChRole && this.editSelection.contentHubEnabled !== previousChEnabled
			if (this.showContentHubSection && (chRoleChanged || chEnabledChanged)) {
				const role = this.editSelection.contentHubEnabled ? this.editSelection.contentHubRole : null
				request.contentHub = {
					enabled: !!role,
					role: role ?? undefined,
					roleName: role ? this.chRoles.roles.find(r => r.id === role)?.name : undefined,
				}
			}
			// on create, a disabled section means "never touched a system that doesn't exist yet" —
			// sending a disable would provision a pointless revoke on a brand-new operator
			const sendDisabled = this.dialogMode !== 'create'
			if (JSON.stringify(this.editSelection.reportCenter) !== JSON.stringify(previous.reportCenter ?? this.emptyReportCenter())) {
				if (this.editSelection.reportCenter.enabled || sendDisabled) request.reportCenter = this.editSelection.reportCenter
			}
			if (this.showPeakSection) {
				const oldPeak = { enabled: !!previous.peaksolution?.enabled, role: previous.peaksolution?.role ?? null }
				if (JSON.stringify(this.editSelection.peaksolution) !== JSON.stringify(oldPeak)) {
					if (this.editSelection.peaksolution.enabled || sendDisabled) request.peaksolution = { enabled: this.editSelection.peaksolution.enabled, role: this.editSelection.peaksolution.role ?? undefined }
				}
			}
			return request
		},
		hasPermissionSelections(request) {
			return request.applications.length || request.tags.length || request.contentHub || request.reportCenter || request.peaksolution
		},
		async saveOperator() {
			this.loading = true
			try {
				const executive = new OperatorsExecutive(this)
				const clientId = this.$store.state.selectedClient.sys.id

				let userId
				// the create baseline mirrors showAddOperator's defaults (sections toggled on but
				// unconfigured), so only sections the user actually configured produce a diff
				let previous = {
					contentHubRole: null,
					reportCenter: { ...this.emptyReportCenter(), enabled: true },
					peaksolution: { enabled: true, role: null },
					twoFactorRequired: false,
				}
				let recoveryEmailSent = true

				if (this.dialogMode === 'create') {
					const created = await executive.createOperator(clientId, {
						email: this.account.email,
						name: this.account.name,
						preferredLanguage: this.account.preferredLanguage,
					})
					userId = created.id
					recoveryEmailSent = created.recoveryEmailSent
				}
				else {
					userId = this.selectedOperator.id
					previous = this.selectedOperator
				}

				const request = this.buildPermissionsRequest(userId, previous)
				if (this.dialogMode === 'edit' || this.hasPermissionSelections(request)) {
					await executive.updateOperatorPermissions(clientId, request)
				}
				if (this.editSelection.twoFactorRequired !== (previous.twoFactorRequired ?? false)) {
					await executive.setTwoFactor(clientId, { userId, enabled: this.editSelection.twoFactorRequired })
				}

				this.$refs.operatorDialog.show = false
				this.successTitle = this.$t('text.SUCCESS')
				this.successDetail = this.dialogMode === 'create'
					? (recoveryEmailSent ? this.$t('text.operatorCreated') : this.$t('text.operatorCreatedEmailFailed'))
					: this.$t('text.operatorPermissionsSaved')
				await this.getOperators()
			}
			catch (error) {
				if (error.response?.status == 401) return this.$emit('show-login')
				this.showError(error)
			}
			this.loading = false
		},
		async resendRecovery() {
			this.loading = true
			try {
				const executive = new OperatorsExecutive(this)
				const userId = this.selectedOperator.id
				await executive.resendRecoveryEmail(this.$store.state.selectedClient.sys.id, { userId })
				this.successTitle = this.$t('text.SUCCESS')
				this.successDetail = this.$t('text.recoveryEmailSentSuccess')
				this.recoveryCooldown[userId] = true
				setTimeout(() => { delete this.recoveryCooldown[userId] }, 60 * 1000)
			}
			catch (error) {
				if (error.response?.status == 401) return this.$emit('show-login')
				if (error instanceof RecoveryEmailRateLimitedError) {
					this.loading = false
					return this.showError(this.$t('text.recoveryEmailRateLimited', { minutes: Math.ceil(error.retryAfterSeconds / 60) }))
				}
				this.showError(error)
			}
			this.loading = false
		},
		async toggleEnabled() {
			this.loading = true
			try {
				const executive = new OperatorsExecutive(this)
				const enabled = !this.selectedOperator.enabled
				await executive.setOperatorEnabled(this.$store.state.selectedClient.sys.id, { userId: this.selectedOperator.id, enabled })
				this.selectedOperator.enabled = enabled
				await this.getOperators()
			}
			catch (error) {
				if (error.response?.status == 401) return this.$emit('show-login')
				this.showError(error)
			}
			this.loading = false
		},
		async search() {
			this.offset = 0
			await this.getOperators()
		},
		async clearSearch() {
			this.offset = 0
			this.searchString = ''
			await this.getOperators()
		},
	},
	async mounted() {
		if (!this.hasFeature) {
			this.errorTitle = this.$t('text.ERROR')
			this.errorDetail = this.$t('text.operatorsFeatureNotEnabled')
			return
		}
		this.getOperators()
		// section availability comes from the client's system config (CH space / PEAK installation);
		// re-ensured on every dialog open in case this initial load fails or the client changes
		this.ensureDialogData()
	},
}
</script>

<style scoped>
/* one column by default; on wide screens the system sections flow into a 2-column grid
   while the account header/fields keep spanning the full width */
/* minmax(0, 1fr): a plain 1fr track never shrinks below its content's min-width, so one
   long provisioning error message used to push the second column out of the dialog */
.operator-dialog { padding: 10px; display: grid; grid-template-columns: minmax(0, 1fr); gap: 12px; width: 100%; }
.operator-dialog > .Disclosure { min-width: 0; }
.operator-dialog > .account-create, .operator-dialog > .account-header { grid-column: 1 / -1; }
/* the history spans the full width below the module cards and is never dimmed by suspension */
.operator-dialog > .history-section { grid-column: 1 / -1; }
/* row partners share the row height so the grid reads as uniform boxes: stretch the
   Disclosure to the grid track and let its panel card fill the stretched box */
.operator-dialog > .Disclosure { height: 100%; }
.operator-dialog > .Disclosure :deep(.v-expansion-panel) { flex: 1 1 auto; }
/* compact section headers: no letter-wrapped titles (Disclosure's break-all), less padding;
   the title stays on one line, status messages wrap inside the column */
.operator-dialog > .Disclosure :deep(.v-expansion-panel-title) { word-break: normal; }
.operator-dialog > .Disclosure :deep(.v-expansion-panel-title .column) { min-width: 0; }
.operator-dialog > .Disclosure :deep(.small-title) { white-space: nowrap; }
.section-error { display: flex; align-items: flex-start; gap: 6px; margin-bottom: 10px; padding: 8px 10px; border-radius: 4px;
	background: #fdecea; color: #b00020; font-size: 12px; line-height: 1.4; overflow-wrap: anywhere; }
.section-error .v-icon { flex-shrink: 0; margin-top: 1px; }
/* icon button + quiet caption; the caption may wrap, the button never does */
.peak-login { margin-top: 10px; display: flex; align-items: center; gap: 8px; }
.peak-login-label { font-size: 12px; color: #666; }
.operator-dialog > .Disclosure :deep(.small-title) { font-size: 16px !important; font-weight: 600; }
.operator-dialog > .Disclosure :deep(.disclosureHeader) { padding-left: 0.5rem; padding-right: 0.5rem; }
@media screen and (min-width: 1200px) {
	.operator-dialog { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
}
.field-label { font-size: 12px; color: #666; padding-bottom: 2px; }
.flexible-pricing-status { margin-top: 8px; }
.flexible-pricing-status .section-error { margin-top: 6px; }
.out-of-scope { margin-top: 6px; padding: 6px; background: #f5f5f5; border-radius: 4px; }
.hint { font-size: 11px; color: #888; padding-bottom: 4px; }

.account-create { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 15px; }
.account-create-2fa { display: flex; align-items: flex-end; }
.account-header { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.account-header .disabled-hint { flex: 1 0 100%; display: flex; align-items: center; gap: 6px; font-size: 10pt; color: #646464; }
/* suspended account: the sections are locked — dim them so the state is unmistakable
   (the header banner + lock icons carry the explanation) */
.operator-dialog.account-suspended > .Disclosure:not(.history-section) { opacity: 0.55; filter: grayscale(0.5); }
.account-identity { font-weight: bold; }
.account-header .account-2fa { flex: 0 0 auto; }
.account-actions { margin-left: auto; display: flex; gap: 8px; flex-wrap: wrap; }

/* margin-right separates the accessory (chip + switch) from the expansion arrow */
.header-accessory { display: flex; align-items: center; gap: 10px; margin-right: 14px; flex-shrink: 0; }
.app-badge { display: inline-flex; margin-right: 4px; vertical-align: middle; }
/* the two scope columns keep the five RC dimension fields from stacking into a tower */
.scope-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 15px; }
@media screen and (max-width: 800px) {
	.account-create, .scope-grid { grid-template-columns: 1fr; }
}
/* once the sections sit in the 2-column grid, each is half-width — stack the scope fields */
@media screen and (min-width: 1200px) {
	.scope-grid { grid-template-columns: 1fr; }
}
</style>

<style>
/* v-tooltip teleports to the overlay container, so these cannot be scoped */
.system-chip-tooltip { max-width: 440px; }
.system-chip-tooltip .scope-rows { margin: 4px 0 0; padding-left: 18px; }
/* a global list style paints li/b dark — keep everything on the tooltip's white */
.system-chip-tooltip .scope-rows li,
.system-chip-tooltip .scope-rows b { color: #fff; margin-top: 2px; }
</style>
